import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { useCreateProduct } from '@/hooks/useProducts';
import { ProductCreateData } from '@/services/products.service';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

interface CreateProductModalProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
}

export function CreateProductModal({ open, onOpenChange }: CreateProductModalProps) {
  const navigate = useNavigate();
  const createProductMutation = useCreateProduct();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<Partial<ProductCreateData>>({
    name: '',
    description: '',
    version: '1.0.0',
    status: 'draft',
    visibility: 'public',
  });

  const handleCreate = async () => {
    if (!formData.name?.trim()) {
      setError(t('products.createProduct.validation.nameRequired'));
      return;
    }

    setError(null);

    // Create the product using the mutation hook
    createProductMutation.mutate({
      name: formData.name,
      description: formData.description || '',
      version: formData.version || '1.0.0',
      status: 'draft',
      visibility: formData.visibility ?? 'public',
    } as ProductCreateData, {
      onSuccess: (product) => {
        // Close modal
        onOpenChange(false);
        
        // Navigate to product details
        navigate(`/provider/products/${product.id}`);
      },
      onError: () => {
        setError(t('products.createProduct.errorMessage'));
      }
    });
  };

  const handleClose = () => {
    if (!createProductMutation.isPending) {
      setFormData({
        name: '',
        description: '',
        version: '1.0.0',
        status: 'draft',
        visibility: 'public',
      });
      setError(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('products.createProduct.title')}</DialogTitle>
          <DialogDescription>{t('products.createProduct.subtitle')}</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="name">
              {t('products.createProduct.nameLabel')}
              <span className="text-destructive"> *</span>
            </Label>
            <Input
              id="name"
              placeholder={t('products.createProduct.namePlaceholder')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              disabled={createProductMutation.isPending}
              autoFocus
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">{t('products.createProduct.descriptionLabel')}</Label>
            <Textarea
              id="description"
              placeholder={t('products.createProduct.descriptionPlaceholder')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              disabled={createProductMutation.isPending}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="visibility">{t('products.productVisibilityLabel')}</Label>
            <Select
              value={formData.visibility ?? 'public'}
              onValueChange={(value) => setFormData({ ...formData, visibility: value as ProductCreateData['visibility'] })}
              disabled={createProductMutation.isPending}
            >
              <SelectTrigger id="visibility">
                <SelectValue placeholder={t('products.visibilityLabel')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">
                  {t('products.visibilityOptions.public')}
                </SelectItem>
                <SelectItem value="private">
                  {t('products.visibilityOptions.private')}
                </SelectItem>
              </SelectContent>
            </Select>
            <div className="rounded-md border p-3 text-sm text-muted-foreground space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{t('products.visibilityOptions.public')}</Badge>
                <span>{t('products.visibilityNotes.public')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{t('products.visibilityOptions.private')}</Badge>
                <span>{t('products.visibilityNotes.private')}</span>
              </div>
            </div>
          </div>
          
          <div className="text-sm text-muted-foreground">
            {t('products.createProduct.draftInfo')}{' '}
            <strong>{t('products.createProduct.draftLabel')}</strong>.{' '}
            {t('products.createProduct.draftFollowup')}
          </div>
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={createProductMutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={createProductMutation.isPending || !formData.name?.trim()}
          >
            {createProductMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('products.createProduct.creatingLabel')}
              </>
            ) : (
              t('products.createProduct.submitLabel')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
